
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";

export interface DaySchedule {
  enabled: boolean;
  start: string;
  end: string;
}

export interface WeeklySchedule {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

export interface Service {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  category_id: string | null;
  price: number;
  pricing_type: "fixed" | "hourly";
  delivery_method?: "virtual" | "in-person" | "both" | null;
  service_location?: {
    latitude: number;
    longitude: number;
    radius: number;
  } | null;
  availability_schedule?: WeeklySchedule | null;
  image_url?: string | null;
  created_at: string;
  updated_at: string;
  service_categories?: ServiceCategory;
}

export interface ServiceCategory {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
}

interface CreateServiceData {
  title: string;
  description?: string;
  category_id?: string;
  price: number;
  pricing_type: "fixed" | "hourly";
  delivery_method?: "virtual" | "in-person" | "both";
  service_location?: {
    latitude: number;
    longitude: number;
    radius: number;
  } | null;
  availability_schedule?: WeeklySchedule | null;
  image_url?: string | null;
}

interface UpdateServiceData {
  title?: string;
  description?: string;
  category_id?: string;
  price?: number;
  pricing_type?: "fixed" | "hourly";
  delivery_method?: "virtual" | "in-person" | "both";
  service_location?: {
    latitude: number;
    longitude: number;
    radius: number;
  } | null;
  availability_schedule?: WeeklySchedule | null;
  image_url?: string | null;
}

// Filter options interface
export interface ServiceFilters {
  searchQuery?: string;
  categoryId?: string;
  priceRange?: string;
  deliveryMethod?: "virtual" | "in-person" | "both" | "any";
}

export function useServices() {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<ServiceFilters>({});

  // Function to update filters
  const updateFilters = (newFilters: Partial<ServiceFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Function to reset filters
  const resetFilters = () => {
    setFilters({});
  };

  // Fetch all categories - accessible to both authenticated and unauthenticated users
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['serviceCategories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('service_categories')
        .select('*')
        .order('name');
      
      if (error) {
        console.error("Error fetching service categories:", error);
        toast.error("Failed to load service categories");
        throw error;
      }
      
      return data as ServiceCategory[];
    },
  });

  // Fetch all services (for browsing) with filters - accessible to both authenticated and unauthenticated users
  const { data: services, isLoading: servicesLoading, refetch: refetchServices } = useQuery({
    queryKey: ['services', filters],
    queryFn: async () => {
      console.log("Fetching services with filters:", filters);
      
      let query = supabase
        .from('services')
        .select(`
          *,
          service_categories:category_id (
            id,
            name,
            description,
            created_at
          )
        `);
      
      // Apply category filter
      if (filters.categoryId) {
        query = query.eq('category_id', filters.categoryId);
      }
      
      // Apply delivery method filter
      if (filters.deliveryMethod && filters.deliveryMethod !== 'any') {
        query = query.eq('delivery_method', filters.deliveryMethod);
      }
      
      // Apply price range filter
      if (filters.priceRange) {
        const [min, max] = filters.priceRange.split('-').map(Number);
        if (!isNaN(min) && !isNaN(max)) {
          query = query.gte('price', min).lte('price', max);
        } else if (!isNaN(min) && filters.priceRange.includes('+')) {
          query = query.gte('price', min);
        }
      }
      
      // Apply search query filter
      if (filters.searchQuery && filters.searchQuery.trim() !== '') {
        query = query.or(`title.ilike.%${filters.searchQuery}%,description.ilike.%${filters.searchQuery}%`);
      }
      
      // Execute query
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching services:", error);
        toast.error("Failed to load services");
        throw error;
      }
      
      // Ensure proper typing of the returned data with service_location parsing
      return data.map(service => {
        let serviceLocation = null;
        if (service.service_location) {
          try {
            serviceLocation = typeof service.service_location === 'string' 
              ? JSON.parse(service.service_location) 
              : service.service_location;
          } catch (e) {
            console.error("Error parsing service location:", e);
          }
        }
        
        let availabilitySchedule = null;
        if (service.availability_schedule) {
          try {
            availabilitySchedule = typeof service.availability_schedule === 'string'
              ? JSON.parse(service.availability_schedule)
              : service.availability_schedule;
          } catch (e) {
            console.error("Error parsing availability schedule:", e);
          }
        }
        
        return {
          ...service,
          pricing_type: service.pricing_type as "fixed" | "hourly",
          delivery_method: service.delivery_method as "virtual" | "in-person" | "both" | null,
          service_location: serviceLocation,
          availability_schedule: availabilitySchedule as WeeklySchedule | null,
          image_url: service.image_url || null,
          service_categories: service.service_categories ? {
            ...service.service_categories,
            created_at: service.service_categories.created_at || ''
          } : undefined
        };
      }) as Service[];
    },
  });

  // Fetch services by user ID - requires authentication
  const getUserServices = async (userId: string): Promise<Service[]> => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('services')
        .select(`
          *,
          service_categories:category_id (
            id,
            name,
            description,
            created_at
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Properly type cast the returned data with service_location parsing
      return data.map(service => {
        let serviceLocation = null;
        if (service.service_location) {
          try {
            serviceLocation = typeof service.service_location === 'string' 
              ? JSON.parse(service.service_location) 
              : service.service_location;
          } catch (e) {
            console.error("Error parsing service location:", e);
          }
        }
        
        let availabilitySchedule = null;
        if (service.availability_schedule) {
          try {
            availabilitySchedule = typeof service.availability_schedule === 'string'
              ? JSON.parse(service.availability_schedule)
              : service.availability_schedule;
          } catch (e) {
            console.error("Error parsing availability schedule:", e);
          }
        }
        
        return {
          ...service,
          pricing_type: service.pricing_type as "fixed" | "hourly",
          delivery_method: service.delivery_method as "virtual" | "in-person" | "both" | null,
          service_location: serviceLocation,
          availability_schedule: availabilitySchedule as WeeklySchedule | null,
          image_url: service.image_url || null,
          service_categories: service.service_categories ? {
            ...service.service_categories,
            created_at: service.service_categories.created_at || ''
          } : undefined
        };
      }) as Service[];
    } catch (error) {
      console.error("Error fetching user services:", error);
      toast.error("Failed to load user services");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Create a new service
  const createServiceMutation = useMutation({
    mutationFn: async (serviceData: CreateServiceData) => {
      console.log("[CREATE SERVICE] Starting with data:", serviceData);
      
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error("[CREATE SERVICE] Authentication error:", userError);
        throw new Error("You must be logged in to create a service");
      }
      
      if (!userData?.user) {
        console.error("[CREATE SERVICE] No user data found");
        throw new Error("You must be logged in to create a service");
      }
      
      console.log("[CREATE SERVICE] Authenticated user:", userData.user.id);
      
      // Check if this is user's first service and create shell Stripe account if needed
      try {
        const { data: existingServices } = await supabase
          .from('services')
          .select('id')
          .eq('user_id', userData.user.id)
          .limit(1);
        
        const { data: profile } = await supabase
          .from('profiles')
          .select('stripe_account_id')
          .eq('id', userData.user.id)
          .single();
        
        // If user has no services and no Stripe account, create shell account
        if ((!existingServices || existingServices.length === 0) && !profile?.stripe_account_id) {
          console.log("[CREATE SERVICE] First service - creating shell Stripe account");
          try {
            const { error: shellError } = await supabase.functions.invoke('create-stripe-shell-account');
            if (shellError) {
              console.warn("[CREATE SERVICE] Shell account creation failed (non-blocking):", shellError);
            } else {
              console.log("[CREATE SERVICE] Shell Stripe account created successfully");
            }
          } catch (shellErr) {
            // Non-blocking - don't fail service creation if shell account fails
            console.warn("[CREATE SERVICE] Shell account creation error (non-blocking):", shellErr);
          }
        }
      } catch (checkErr) {
        console.warn("[CREATE SERVICE] Error checking for shell account (non-blocking):", checkErr);
      }
      
      // Validate required fields
      if (!serviceData.title) {
        throw new Error("Service title is required");
      }
      
      if (serviceData.price === undefined || serviceData.price === null) {
        throw new Error("Service price is required");
      }
      
      if (!serviceData.pricing_type) {
        console.log("[CREATE SERVICE] No pricing_type provided, defaulting to 'fixed'");
        serviceData.pricing_type = "fixed";
      }
      
      // Validate location requirement
      if ((serviceData.delivery_method === "in-person" || serviceData.delivery_method === "both") && !serviceData.service_location) {
        throw new Error("Service location is required for in-person or hybrid delivery methods");
      }
      
      // Prepare data for insertion - round price to 2 decimal places to avoid floating-point issues
      const serviceToInsert = {
        title: serviceData.title,
        description: serviceData.description || null,
        category_id: serviceData.category_id || null,
        price: Math.round(serviceData.price * 100) / 100,
        pricing_type: serviceData.pricing_type,
        delivery_method: serviceData.delivery_method || 'virtual',
        service_location: serviceData.service_location ? JSON.stringify(serviceData.service_location) : null,
        availability_schedule: (serviceData.availability_schedule as unknown as Json) || null,
        image_url: serviceData.image_url || null,
        user_id: userData.user.id
      };
      
      console.log("[CREATE SERVICE] Inserting service with payload:", serviceToInsert);
      
      // Insert the new service
      const { data, error } = await supabase
        .from('services')
        .insert([serviceToInsert])
        .select();
      
      if (error) {
        console.error("[CREATE SERVICE] Supabase error:", error);
        console.error("  - Error code:", error.code);
        console.error("  - Error message:", error.message);
        console.error("  - Error details:", error.details);
        throw new Error(`Failed to create service: ${error.message}`);
      }
      
      console.log("[CREATE SERVICE] Success! Created service:", data);
      return data[0];
    },
    onSuccess: () => {
      toast.success("Service created successfully!");
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (error) => {
      console.error("[CREATE SERVICE] Error in mutation:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create service");
    }
  });

  // Update an existing service
  const updateServiceMutation = useMutation({
    mutationFn: async ({ id, serviceData }: { id: string, serviceData: UpdateServiceData }) => {
      // Prepare update data (normalize optional string fields)
      // Round price to 2 decimal places to avoid floating-point issues
      const updatePayload = {
        ...serviceData,
        ...(serviceData.price !== undefined && {
          price: Math.round(serviceData.price * 100) / 100,
        }),
        ...(serviceData.category_id !== undefined && {
          category_id: serviceData.category_id && serviceData.category_id.trim() !== "" ? serviceData.category_id : null,
        }),
        ...(serviceData.description !== undefined && {
          description: serviceData.description && serviceData.description.trim() !== "" ? serviceData.description : null,
        }),
        ...(serviceData.image_url !== undefined && {
          image_url: serviceData.image_url && serviceData.image_url.trim() !== "" ? serviceData.image_url : null,
        }),
        ...(serviceData.service_location !== undefined && {
          service_location: serviceData.service_location ? JSON.stringify(serviceData.service_location) : null,
        }),
        ...(serviceData.availability_schedule !== undefined && {
          availability_schedule: (serviceData.availability_schedule as unknown as Json) || null,
        }),
      };

      const { data, error } = await supabase
        .from('services')
        .update(updatePayload)
        .eq('id', id)
        .select();

      if (error) {
        console.error("Error updating service:", error);
        throw error;
      }

      return data[0];
    },
    onSuccess: () => {
      toast.success("Service updated successfully!");
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (error) => {
      console.error("Error updating service:", error);
      toast.error("Failed to update service");
    }
  });

  // Delete a service
  const deleteServiceMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error("Error deleting service:", error);
        throw error;
      }
      
      return id;
    },
    onSuccess: () => {
      toast.success("Service deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (error) => {
      console.error("Error deleting service:", error);
      toast.error("Failed to delete service");
    }
  });

  return {
    // Data
    categories,
    services,
    filters,
    
    // Loading states
    isLoading: loading || servicesLoading,
    isCategoriesLoading: categoriesLoading,
    
    // Methods
    getUserServices,
    createService: createServiceMutation.mutate,
    createServiceAsync: createServiceMutation.mutateAsync,
    updateService: updateServiceMutation.mutate,
    updateServiceAsync: updateServiceMutation.mutateAsync,
    deleteService: deleteServiceMutation.mutate,
    deleteServiceAsync: deleteServiceMutation.mutateAsync,
    updateFilters,
    resetFilters,
    refetchServices
  };
}
